/**
 * Idempotency keys for consequential actions.
 *
 * Package rules: "Duplicate-submit protection, timeout, partial success, retry
 * and rollback" and "preventing double-submit while preserving retry".
 *
 * A key is derived from the operation and its payload, so pressing Submit twice
 * replays the first result instead of creating a second payment — while an
 * explicit retry after a failure uses a fresh attempt token and is therefore a
 * genuinely new attempt, as the payment state machine requires.
 */

let counter = 0;

/** Fresh key for a new, deliberate attempt. */
export function newIdempotencyKey(prefix = 'idem'): string {
  counter += 1;
  return `${prefix}_${String(counter).padStart(8, '0')}`;
}

export function resetIdempotency(): void {
  counter = 0;
  store.clear();
}

type Entry<T> = { readonly status: 'in-flight' | 'settled'; readonly value?: T };

const store = new Map<string, Entry<unknown>>();

/**
 * Run `fn` at most once per key. A concurrent or repeated call with the same
 * key returns the original outcome rather than executing again.
 */
export async function once<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const existing = store.get(key);
  if (existing?.status === 'settled') {
    return existing.value as T;
  }
  if (existing?.status === 'in-flight') {
    throw new DuplicateSubmissionError(key);
  }
  store.set(key, { status: 'in-flight' });
  try {
    const value = await fn();
    store.set(key, { status: 'settled', value });
    return value;
  } catch (err) {
    // A failed attempt does not burn the key: the user may legitimately retry,
    // and that retry is recorded as a separate attempt by the caller.
    store.delete(key);
    throw err;
  }
}

export class DuplicateSubmissionError extends Error {
  readonly code = 'DUPLICATE_SUBMISSION';
  constructor(readonly key: string) {
    super('This action is already being processed.');
    this.name = 'DuplicateSubmissionError';
  }
}

export function hasSettled(key: string): boolean {
  return store.get(key)?.status === 'settled';
}
